import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkEmoji from 'remark-emoji';
import { useParams } from 'react-router-dom';
import CyberNav from '../CyberNav/CyberNav';
import ArticlesList from './ArticlesList';

// Import shared utilities and components
import { detectLanguageDirection, wrapWithLanguage } from '../../../utils/languageUtils';
import { extractMetadata, sanitizeHtml, ArticleMetadata } from '../../../utils/markdownUtils';
import useResponsive from '../../../utils/useResponsive';
import ErrorBoundary from '../../Shared/ErrorBoundary';
import CodeBlock from '../../Shared/CodeBlock';

// CSS imports
import './Markdown-Global.css';    // 1. Base styles
import './CyberPages.css';        // 2. Cyber specific layout

// No local implementations needed - using shared components and utilities

const CyberGuides = () => {
  const { fileName } = useParams();
  const [content, setContent] = useState('');
  const [metadata, setMetadata] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBox, setShowBox] = useState(true);
  // const { isMobile } = useResponsive();

  useEffect(() => {
    if (fileName) {
      const fetchContent = async () => {
        setLoading(true);
        try {
          const response = await fetch(`/md/CyberGuides/${fileName}`);
          if (!response.ok) throw new Error('Content not found');
          const text = await response.text();
          const { content: parsedContent, metadata: parsedMetadata } = extractMetadata(text);
          setContent(parsedContent);
          setMetadata(parsedMetadata);
          setError(null);
        } catch (err) {
          setError(err.message);
          setContent('');
          setMetadata({});
        } finally {
          setLoading(false);
        }
      };

      fetchContent();
    }
  }, [fileName]);

  return (
    <div className="cyber-wrapper">
      {!fileName && showBox && (
        <div className="rtl-box">
          <button 
            className="close-btn" 
            onClick={() => setShowBox(false)}
            aria-label="Close welcome message"
          >
            ×
          </button>
          <p>ברוכים הבאים למדריכי הסייבר שלנו! כאן תמצאו מידע מקיף על אבטחת מידע והגנת סייבר.</p>
          <p>המדריכים מכסים נושאים מגוונים, החל מיסודות האבטחה ועד לטכניקות מתקדמות.</p>
        </div>
      )}
      
      <div className="cyber-page-layout">
        <CyberNav />
        {fileName ? (
          <div className="markdown-content" lang={detectLanguageDirection(content)}>
            {loading ? (
              <div className="skeleton-loader">
                <div className="skeleton-header"></div>
                <div className="skeleton-text"></div>
                <div className="skeleton-text"></div>
                <div className="skeleton-text"></div>
              </div>
            ) : error ? (
              <div className="error-container">
                <p className="error">{error}</p>
                <p>Try refreshing the page or contact support if the problem persists.</p>
              </div>
            ) : (
              <>
                <ArticleMetadata metadata={metadata} />
                <ErrorBoundary>
                  <ReactMarkdown
                    children={sanitizeHtml(content)}
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm, remarkEmoji]}
                    components={{
                      h1: ({node, children, ...props}) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h1 {...props}>{wrapWithLanguage(children, lang, true)}</h1>;
                      },
                      h2: ({node, children, ...props}) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h2 {...props}>{wrapWithLanguage(children, lang, true)}</h2>;
                      },
                      h3: ({node, children, ...props}) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h3 {...props}>{wrapWithLanguage(children, lang, true)}</h3>;
                      },
                      p: ({node, children, ...props}) => {
                        const lang = detectLanguageDirection(String(children));
                        return <p {...props} lang={lang}>{wrapWithLanguage(children, lang, false)}</p>;
                      },
                      code: ({node, inline, className, children, ...props}) => {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <CodeBlock className={className} {...props}>
                            {children}
                          </CodeBlock>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  />
                </ErrorBoundary>
              </>
            )}
          </div>
        ) : (
          <ErrorBoundary>
            <ArticlesList 
              folderName="CyberGuides"
              basePath="cyber/hacking/guides"
              defaultCategory="Cyber Guide"
            />
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
};

export default CyberGuides;
